<template>
  <div class="min-h-screen bg-gray-50 text-gray-800" dir="rtl">
    <!-- Top Bar -->
    <header class="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500"/>
          <div>
            <h1 class="text-xl md:text-2xl font-extrabold bg-clip-text text-transparent bg-gradient-to-l from-purple-600 via-pink-500 to-blue-600">
              داشبورد مالی تأمین‌کننده
            </h1>
            <p class="text-xs text-gray-500">ثبت پرداخت‌ها، چک‌ها، و گزارش‌های لحظه‌ای</p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button @click="initLoad" class="px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800 text-sm">
            بروزرسانی
          </button>
        </div>
      </div>

      <!-- Require supplier selection ribbon (no search, just select) -->
      <div v-if="!hasSupplier" class="max-w-7xl mx-auto px-4 pb-3">
        <div class="rounded-xl border border-amber-300 bg-amber-50 p-3 text-amber-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div class="text-sm">لطفاً ابتدا تأمین‌کننده را انتخاب کنید. سپس گزارش‌ها و فرم‌ها فعال می‌شوند.</div>
          <div class="w-full sm:w-80">
            <select v-model="selectedSupplierId" @change="onSupplierPicked"
                    class="w-full rounded-xl border-gray-300 px-3 py-2 bg-white">
              <option disabled value="">انتخاب تأمین‌کننده…</option>
              <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <nav class="max-w-7xl mx-auto px-4">
        <ul class="flex gap-2 overflow-x-auto pb-2">
          <li v-for="t in tabs" :key="t.value">
            <button
                :disabled="!hasSupplier && t.value!=='create'"
                :class="[
                'px-3 py-2 rounded-xl text-sm transition whitespace-nowrap',
                activeTab===t.value ? 'bg-gray-900 text-white shadow' : 'bg-gray-100 hover:bg-gray-200',
                (!hasSupplier && t.value!=='create') ? 'opacity-50 cursor-not-allowed' : ''
              ]"
                @click="activeTab=t.value">{{ t.label }}
            </button>
          </li>
        </ul>
      </nav>
    </header>

    <main class="max-w-7xl mx-auto px-4 py-6 grid lg:grid-cols-12 gap-6">
      <!-- Left Side: Filters & KPIs -->
      <aside class="lg:col-span-4 space-y-6">
        <!-- Smart Filters -->
        <section class="bg-white rounded-2xl border border-gray-200 p-4">
          <div class="flex items-center justify-between mb-3">
            <h3 class="font-semibold">فیلترهای هوشمند</h3>
            <button @click="resetFilters" class="text-xs text-gray-500 hover:text-gray-800">ریست</button>
          </div>
          <div class="space-y-3">
            <!-- Supplier selector (no search) -->
            <div>
              <label class="block text-sm text-gray-600 mb-1">تأمین‌کننده</label>
              <select v-model="selectedSupplierId" @change="onSupplierPicked"
                      class="w-full rounded-xl border-gray-300 px-3 py-2 bg-white">
                <option disabled value="">انتخاب…</option>
                <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
              <div v-if="filters.supplier" class="mt-2 flex items-center gap-2">
                <span class="px-2 py-0.5 rounded-lg bg-gray-100 text-xs">{{ filters.supplier.name }}</span>
                <button @click="clearSupplier" class="text-xs text-gray-500 hover:text-gray-800">حذف</button>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-sm text-gray-600 mb-1">از تاریخ</label>
                <input type="date" v-model="filters.date_from"
                       class="w-full rounded-xl border-gray-300 focus:ring-2 focus:ring-purple-400 px-3 py-2"/>
              </div>
              <div>
                <label class="block text-sm text-gray-600 mb-1">تا تاریخ</label>
                <input type="date" v-model="filters.date_to"
                       class="w-full rounded-xl border-gray-300 focus:ring-2 focus:ring-purple-400 px-3 py-2"/>
              </div>
            </div>

            <div class="flex flex-wrap gap-2 text-xs">
              <button v-for="p in presets" :key="p.label" @click="applyPreset(p)"
                      class="px-2 py-1 rounded-lg bg-gray-100 hover:bg-gray-200">{{ p.label }}</button>
            </div>

            <div class="flex items-center justify-between">
              <button :disabled="!hasSupplier" @click="refreshAllReports"
                      class="px-3 py-2 rounded-xl bg-gray-900 text-white text-sm disabled:opacity-50">اعمال فیلتر</button>
              <button @click="exportAllCSV" class="px-3 py-2 rounded-xl bg-white border text-sm hover:bg-gray-50">
                خروجی CSV
              </button>
            </div>
          </div>
        </section>

        <!-- KPI Cards -->
        <section class="grid grid-cols-2 gap-3">
          <div class="bg-white rounded-2xl border p-4">
            <div class="text-xs text-gray-500">مانده انتخابی</div>
            <div class="text-2xl font-extrabold ltr"
                 :class="Number(ledger.final_balance)>0 ? 'text-red-600' : 'text-emerald-600'">
              {{ money(ledger.final_balance) }}
            </div>
          </div>
          <div class="bg-white rounded-2xl border p-4">
            <div class="text-xs text-gray-500">جمع پرداخت‌ها (لیست)</div>
            <div class="text-2xl font-extrabold ltr">{{ money(sumArray(paymentRegister.map(p => p.amount))) }}</div>
          </div>
          <div class="bg-white rounded-2xl border p-4">
            <div class="text-xs text-gray-500">Aging 30-</div>
            <div class="text-xl font-bold ltr">{{ money(agingData.d30) }}</div>
          </div>
          <div class="bg-white rounded-2xl border p-4">
            <div class="text-xs text-gray-500">Aging 90+</div>
            <div class="text-xl font-bold ltr">{{ money(agingData['d90+']) }}</div>
          </div>
        </section>

        <!-- Quick Actions -->
        <section class="bg-white rounded-2xl border p-4 space-y-3">
          <div class="font-semibold">میانبرها</div>
          <div class="grid grid-cols-2 gap-2">
            <button @click="activeTab='create'"
                    class="px-3 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-sm">ثبت پرداخت</button>
            <button :disabled="!hasSupplier" @click="activeTab='payments'"
                    class="px-3 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 text-sm disabled:opacity-50">
              مشاهده پرداخت‌ها
            </button>
          </div>
        </section>
      </aside>

      <!-- Right Side: Tabs Content -->
      <section class="lg:col-span-8 space-y-6">
        <!-- Ledger -->
        <div v-show="activeTab==='ledger'" class="bg-white rounded-2xl border overflow-hidden">
          <div class="flex items-center justify-between px-4 py-3 border-b">
            <h2 class="font-semibold">دفتر حساب تأمین‌کننده</h2>
            <div class="flex items-center gap-2 text-xs">
              <span class="text-gray-500">مانده</span>
              <span class="px-2 py-0.5 rounded-lg bg-gray-100 ltr">{{ money(ledger.final_balance) }}</span>
            </div>
          </div>
          <div class="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table class="min-w-full text-sm">
              <thead class="sticky top-0 bg-white border-b">
              <tr>
                <th class="px-4 py-3 text-right">تاریخ</th>
                <th class="px-4 py-3 text-right">شرح</th>
                <th class="px-4 py-3 text-left">بدهکار</th>
                <th class="px-4 py-3 text-left">بستانکار</th>
                <th class="px-4 py-3 text-left">مانده</th>
              </tr>
              </thead>
              <tbody>
              <tr v-if="loading.ledger">
                <td colspan="5" class="p-6 text-center text-gray-500">در حال بارگذاری…</td>
              </tr>
              <tr v-else-if="!ledger.rows.length">
                <td colspan="5" class="p-8 text-center text-gray-400">رکوردی یافت نشد</td>
              </tr>
              <tr v-for="(r,idx) in ledger.rows" :key="idx" class="border-t hover:bg-gray-50">
                <td class="px-4 py-2">{{ r.date }}</td>
                <td class="px-4 py-2">{{ r.memo }}</td>
                <td class="px-4 py-2 ltr">{{ money(r.debit) }}</td>
                <td class="px-4 py-2 ltr">{{ money(r.credit) }}</td>
                <td class="px-4 py-2 ltr font-semibold">{{ money(r.balance) }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="px-4 py-3 bg-gray-50 border-t flex items-center gap-2 justify-end">
            <button @click="fetchLedger" class="px-3 py-2 rounded-xl bg-gray-900 text-white text-sm">بروزرسانی</button>
            <button @click="exportCSV('ledger', ledger.rows)" class="px-3 py-2 rounded-xl bg-white border text-sm">CSV</button>
          </div>
        </div>

        <!-- Balances -->
        <div v-show="activeTab==='balances'" class="bg-white rounded-2xl border overflow-hidden">
          <div class="flex items-center justify-between px-4 py-3 border-b">
            <h2 class="font-semibold">مانده‌ها</h2>
            <button @click="fetchBalances" class="px-3 py-2 rounded-xl bg-gray-900 text-white text-sm">بروزرسانی</button>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-right">تأمین‌کننده</th>
                <th class="px-4 py-3 text-left">مانده (تومان)</th>
              </tr>
              </thead>
              <tbody>
              <tr v-if="loading.balances">
                <td colspan="2" class="p-6 text-center text-gray-500">در حال بارگذاری…</td>
              </tr>
              <tr v-else-if="!balances.length">
                <td colspan="2" class="p-8 text-center text-gray-400">رکوردی نیست</td>
              </tr>
              <tr v-for="b in balances" :key="b.supplier_id" class="border-t hover:bg-gray-50">
                <td class="px-4 py-2">{{ supplierName(b.supplier_id) }}</td>
                <td class="px-4 py-2 ltr font-semibold"
                    :class="Number(b.balance)>0 ? 'text-red-600' : 'text-emerald-600'">{{ money(b.balance) }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="px-4 py-3 bg-gray-50 border-t flex items-center justify-end">
            <button @click="exportCSV('balances', balances)" class="px-3 py-2 rounded-xl bg-white border text-sm">CSV</button>
          </div>
        </div>

        <!-- Payment Register -->
        <div v-show="activeTab==='payments'" class="bg-white rounded-2xl border overflow-hidden">
          <div class="flex items-center justify-between px-4 py-3 border-b">
            <h2 class="font-semibold">گزارش پرداخت‌ها</h2>
            <div class="flex items-center gap-2">
              <select v-model="paymentFilters.method" class="rounded-xl border-gray-300 text-sm px-3 py-2">
                <option value="">همه روش‌ها</option>
                <option value="cash">نقد</option>
                <option value="cheque">چک</option>
              </select>
              <button @click="fetchPaymentRegister" class="px-3 py-2 rounded-xl bg-gray-900 text-white text-sm">نمایش</button>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-right">شناسه</th>
                <th class="px-4 py-3 text-right">تأمین‌کننده</th>
                <th class="px-4 py-3 text-right">روش</th>
                <th class="px-4 py-3 text-left">تاریخ</th>
                <th class="px-4 py-3 text-left">مبلغ</th>
                <th class="px-4 py-3 text-right">وضعیت</th>
                <th class="px-4 py-3 text-right">چک</th>
              </tr>
              </thead>
              <tbody>
              <tr v-if="loading.paymentRegister">
                <td colspan="7" class="p-6 text-center text-gray-500">در حال بارگذاری…</td>
              </tr>
              <tr v-else-if="!paymentRegister.length">
                <td colspan="7" class="p-8 text-center text-gray-400">پرداختی وجود ندارد</td>
              </tr>
              <tr v-for="p in paymentRegister" :key="p.id" class="border-t hover:bg-gray-50">
                <td class="px-4 py-2 font-mono">{{ p.id.slice(0, 8) }}</td>
                <td class="px-4 py-2">{{ supplierName(p.supplier_id) }}</td>
                <td class="px-4 py-2">{{ p.method === 'cash' ? 'نقد' : 'چکی' }}</td>
                <td class="px-4 py-2">{{ p.date }}</td>
                <td class="px-4 py-2 ltr">{{ money(p.amount) }} {{ p.currency }}</td>
                <td class="px-4 py-2">{{ p.status }}</td>
                <td class="px-4 py-2">
                  <div v-if="p.cheque" class="flex items-center gap-2">
                    <span class="text-xs text-gray-600">شماره: {{ p.cheque.number }}</span>
                    <span class="text-xs">/ سررسید: {{ p.cheque.due_date }}</span>
                    <span class="px-2 py-0.5 rounded-full text-xs"
                          :class="badgeClass(p.cheque.status)">{{ chequeStatusFa(p.cheque.status) }}</span>
                  </div>
                  <span v-else class="text-xs text-gray-400">—</span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="px-4 py-3 bg-gray-50 border-t flex items-center justify-end">
            <button @click="exportCSV('payments', paymentRegister)" class="px-3 py-2 rounded-xl bg-white border text-sm">CSV</button>
          </div>
        </div>

        <!-- Aging -->
        <div v-show="activeTab==='aging'" class="bg-white rounded-2xl border overflow-hidden">
          <div class="flex items-center justify-between px-4 py-3 border-b">
            <h2 class="font-semibold">Aging (۳۰/۶۰/۹۰)</h2>
            <div class="flex items-center gap-2">
              <input type="date" v-model="aging.today" class="rounded-xl border-gray-300 text-sm px-3 py-2"/>
              <button @click="fetchAging" class="px-3 py-2 rounded-xl bg-gray-900 text-white text-sm">نمایش</button>
            </div>
          </div>
          <div class="grid sm:grid-cols-4 grid-cols-2 gap-3 p-4">
            <div class="bg-white rounded-xl border p-4">
              <div class="text-xs text-gray-500">Current</div>
              <div class="text-2xl font-bold ltr">{{ money(agingData.current) }}</div>
            </div>
            <div class="bg-white rounded-xl border p-4">
              <div class="text-xs text-gray-500">30-</div>
              <div class="text-2xl font-bold ltr">{{ money(agingData.d30) }}</div>
            </div>
            <div class="bg-white rounded-xl border p-4">
              <div class="text-xs text-gray-500">60-</div>
              <div class="text-2xl font-bold ltr">{{ money(agingData.d60) }}</div>
            </div>
            <div class="bg-white rounded-xl border p-4">
              <div class="text-xs text-gray-500">90+</div>
              <div class="text-2xl font-bold ltr">{{ money(agingData['d90+']) }}</div>
            </div>
          </div>
        </div>

        <!-- Create Payment / Cheque -->
        <div v-show="activeTab==='create'" class="bg-white rounded-2xl border overflow-hidden">
          <div class="px-4 py-3 border-b flex items-center justify-between">
            <h2 class="font-semibold">ثبت پرداخت / چک</h2>
            <div class="text-xs text-gray-500">اعتبارسنجی زنده و تخصیص هوشمند</div>
          </div>
          <div class="p-4 space-y-6">
            <!-- Form -->
            <div class="grid lg:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-3">
              <div class="lg:col-span-2">
                <label class="block text-sm text-gray-600 mb-1">تأمین‌کننده</label>
                <select v-model="paymentForm.supplier" class="w-full rounded-xl border-gray-300 px-3 py-2 bg-white">
                  <option disabled value="">انتخاب…</option>
                  <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
                </select>
                <p v-if="paymentErrors.supplier" class="text-xs text-red-600 mt-1">{{ paymentErrors.supplier }}</p>
              </div>
              <div>
                <label class="block text-sm text-gray-600 mb-1">ارز</label>
                <select v-model="paymentForm.currency" class="w-full rounded-xl border-gray-300 px-3 py-2 bg-white">
                  <option disabled value="">انتخاب…</option>
                  <option v-for="c in currencies" :key="c.id" :value="c.id">{{ c.name }} ({{ c.code }})</option>
                </select>
                <p v-if="paymentErrors.currency" class="text-xs text-red-600 mt-1">{{ paymentErrors.currency }}</p>
              </div>
              <div>
                <label class="block text-sm text-gray-600 mb-1">روش</label>
                <select v-model="paymentForm.method" class="w-full rounded-xl border-gray-300 px-3 py-2 bg-white">
                  <option value="cash">نقد</option>
                  <option value="cheque">چکی</option>
                </select>
              </div>
              <div>
                <label class="block text-sm text-gray-600 mb-1">تاریخ پرداخت/صدور</label>
                <input type="date" v-model="paymentForm.payment_date" class="w-full rounded-xl border-gray-300 px-3 py-2"/>
                <p v-if="paymentErrors.payment_date" class="text-xs text-red-600 mt-1">{{ paymentErrors.payment_date }}</p>
              </div>
              <div>
                <label class="block text-sm text-gray-600 mb-1">مبلغ کل</label>
                <input type="number" v-model.number="paymentForm.amount" class="w-full rounded-xl border-gray-300 ltr px-3 py-2" placeholder="0.00"/>
                <p v-if="paymentErrors.amount" class="text-xs text-red-600 mt-1">{{ paymentErrors.amount }}</p>
              </div>
            </div>

            <!-- Allocations -->
            <div class="border rounded-2xl p-3">
              <div class="flex items-center justify-between mb-3">
                <div class="font-medium">تخصیص به صورت‌حساب‌ها</div>
                <div class="flex items-center gap-2">
                  <button @click="openBillPicker()" class="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs">انتخاب از لیست</button>
                  <button @click="addAllocRow" class="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs">افزودن ردیف</button>
                </div>
              </div>
              <div v-for="(a,idx) in paymentForm.allocations" :key="idx"
                   class="grid md:grid-cols-6 grid-cols-1 gap-2 items-end mb-2">
                <div class="md:col-span-4">
                  <label class="block text-xs text-gray-500 mb-1">شناسه Bill</label>
                  <input type="text" v-model="a.bill" class="w-full rounded-xl border-gray-300 font-mono text-xs ltr px-3 py-2" placeholder="UUID"/>
                </div>
                <div>
                  <label class="block text-xs text-gray-500 mb-1">مبلغ</label>
                  <input type="number" v-model.number="a.amount" class="w-full rounded-xl border-gray-300 ltr px-3 py-2" placeholder="0.00"/>
                </div>
                <div class="flex items-center gap-2">
                  <button @click="removeAllocRow(idx)" class="px-3 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100">حذف</button>
                </div>
              </div>
              <div class="flex items-center justify-between mt-2 text-xs">
                <div class="text-gray-500">جمع تخصیص</div>
                <div class="font-semibold ltr">{{ money(sumAllocations) }}</div>
              </div>
            </div>

            <!-- Cheque fields -->
            <div v-if="paymentForm.method==='cheque'" class="grid md:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-3">
              <div>
                <label class="block text-sm text-gray-600 mb-1">شماره چک</label>
                <input type="text" v-model="chequeForm.number" class="w-full rounded-xl border-gray-300 px-3 py-2"/>
              </div>
              <div>
                <label class="block text-sm text-gray-600 mb-1">بانک</label>
                <input type="text" v-model="chequeForm.bank_name" class="w-full rounded-xl border-gray-300 px-3 py-2"/>
              </div>
              <div>
                <label class="block text-sm text-gray-600 mb-1">تاریخ صدور</label>
                <input type="date" v-model="chequeForm.issue_date" class="w-full rounded-xl border-gray-300 px-3 py-2"/>
              </div>
              <div>
                <label class="block text-sm text-gray-600 mb-1">تاریخ سررسید</label>
                <input type="date" v-model="chequeForm.due_date" class="w-full rounded-xl border-gray-300 px-3 py-2"/>
              </div>
            </div>

            <div class="flex items-center justify-end gap-3">
              <button @click="resetPaymentForm" class="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200">ریست</button>
              <button v-if="paymentForm.method==='cash'"  @click="submitCashPayment"
                      class="px-4 py-2 rounded-xl bg-gray-900 text-white disabled:opacity-50">ثبت پرداخت نقدی</button>
              <button v-else :disabled="!formValidCheque || submitting" @click="submitChequePayment"
                      class="px-4 py-2 rounded-xl bg-gray-900 text-white disabled:opacity-50">ثبت و صدور چک</button>
            </div>

            <!-- Cheque actions -->
            <div class="border rounded-2xl p-3 space-y-3">
              <div class="font-semibold">عملیات روی چک</div>
              <div class="grid md:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-3">
                <div>
                  <label class="block text-sm text-gray-600 mb-1">شناسه چک</label>
                  <input type="text" v-model="chequeAction.id" class="w-full rounded-xl border-gray-300 font-mono text-xs ltr px-3 py-2" placeholder="UUID"/>
                </div>
                <div>
                  <label class="block text-sm text-gray-600 mb-1">تاریخ وصول/برگشت</label>
                  <input type="date" v-model="chequeAction.date" class="w-full rounded-xl border-gray-300 px-3 py-2"/>
                </div>
                <div class="flex items-end gap-2">
                  <button :disabled="submitting" @click="clearCheque" class="px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700">وصول</button>
                  <button :disabled="submitting" @click="bounceCheque" class="px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700">برگشت</button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <!-- Bill Picker Modal -->
      <transition name="fade">
        <div v-if="billPicker.open" class="fixed inset-0 z-40 flex items-end sm:items-center justify-center">
          <div class="absolute inset-0 bg-black/40" @click="billPicker.open=false"/>
          <div class="relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-xl border m-4 p-4">
            <div class="flex items-center justify-between mb-3">
              <h3 class="font-semibold">انتخاب صورت‌حساب</h3>
              <button class="text-gray-500 hover:text-gray-800" @click="billPicker.open=false">✕</button>
            </div>
            <div class="flex items-center gap-2 mb-3">
              <input type="text" v-model="billPicker.q" @input="debouncedBillSearch" placeholder="جستجو شماره/منبع…"
                     class="w-full rounded-xl border-gray-300 px-3 py-2"/>
              <button @click="searchBills" class="px-3 py-2 rounded-xl bg-gray-900 text-white text-sm">جستجو</button>
            </div>
            <div class="max-h-80 overflow-auto border rounded-xl">
              <table class="min-w-full text-sm">
                <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th class="px-3 py-2 text-right">شناسه</th>
                  <th class="px-3 py-2 text-right">تاریخ</th>
                  <th class="px-3 py-2 text-right">مبلغ</th>
                  <th class="px-3 py-2"></th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="billPicker.loading">
                  <td colspan="4" class="p-6 text-center text-gray-500">در حال جستجو…</td>
                </tr>
                <tr v-else-if="!billPicker.list.length">
                  <td colspan="4" class="p-8 text-center text-gray-400">موردی یافت نشد</td>
                </tr>
                <tr v-for="b in billPicker.list" :key="b.id" class="border-t hover:bg-gray-50">
                  <td class="px-3 py-2 font-mono">{{ b.id.slice(0, 8) }}</td>
                  <td class="px-3 py-2">{{ b.issue_date }}</td>
                  <td class="px-3 py-2 ltr">{{ money(b.amount_total) }}</td>
                  <td class="px-3 py-2 text-left">
                    <button @click="pickBill(b)" class="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs">انتخاب</button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
            <div class="mt-3 text-left">
              <button @click="billPicker.open=false" class="px-3 py-2 rounded-xl bg-white border text-sm">بستن</button>
            </div>
          </div>
        </div>
      </transition>

    </main>
  </div>
</template>

<script>
export default {
  name: 'AccountingDashboard',
  data() {
    return {
      tabs: [
        {label: 'دفتر حساب', value: 'ledger'},
        {label: 'مانده‌ها', value: 'balances'},
        {label: 'پرداخت‌ها', value: 'payments'},
        {label: 'Aging', value: 'aging'},
        {label: 'ثبت پرداخت/چک', value: 'create'},
      ],
      activeTab: 'ledger',

      // Filters & presets
      presets: [
        { label: 'امروز', range(){ const d=new Date(); const s=d.toISOString().slice(0,10); return {from:s, to:s} } },
        { label: '۷ روز اخیر', range(){ const d=new Date(); const t=d.toISOString().slice(0,10); d.setDate(d.getDate()-6); const f=d.toISOString().slice(0,10); return {from:f,to:t} } },
        { label: '۳۰ روز اخیر', range(){ const d=new Date(); const t=d.toISOString().slice(0,10); d.setDate(d.getDate()-29); const f=d.toISOString().slice(0,10); return {from:f,to:t} } },
        { label: 'این ماه', range(){ const d=new Date(); const t=d.toISOString().slice(0,10); const f=`${d.getFullYear()}-${('0'+(d.getMonth()+1)).slice(-2)}-01`; return {from:f,to:t} } },
      ],
      filters: { supplier: null, date_from: '', date_to: '' },

      // Supplier state (no search)
      suppliers: [],           // list from main/supplier/all (max 10)
      selectedSupplierId: '',  // v-model for selects

      currencies: [],
      suppliersCache: [],

      // Reports state
      ledger: {rows: [], final_balance: '0.00'},
      balances: [],
      paymentFilters: {method: ''},
      paymentRegister: [],
      aging: {today: ''},
      agingData: {current: '0', d30: '0', d60: '0', 'd90+': '0'},

      // Create payment form
      paymentForm: {supplier: '', currency: '', method: 'cash', payment_date: '', amount: 0, allocations: []},
      chequeForm: {number: '', bank_name: '', issue_date: '', due_date: ''},
      chequeAction: {id: '', date: ''},
      paymentErrors: {},

      // Bill Picker
      billPicker: {open: false, q: '', list: [], loading: false},
      billTimer: null,

      loading: {ledger: false, balances: false, paymentRegister: false},
      submitting: false,
    }
  },
  computed: {
    hasSupplier(){ return !!(this.filters.supplier && this.filters.supplier.id) },
    sumAllocations() {
      return (this.paymentForm.allocations || []).reduce((s, a) => s + Number(a.amount || 0), 0)
    },
    formValid() {
      this.paymentErrors = {}
      if (!this.paymentForm.supplier) this.paymentErrors.supplier = 'تأمین‌کننده را انتخاب کنید'
      if (!this.paymentForm.currency) this.paymentErrors.currency = 'ارز را انتخاب کنید'
      if (!this.paymentForm.payment_date) this.paymentErrors.payment_date = 'تاریخ را وارد کنید'
      if (!this.paymentForm.amount || Number(this.paymentForm.amount) <= 0) this.paymentErrors.amount = 'مبلغ معتبر نیست'
      if (Number(this.paymentForm.amount) !== Number(this.sumAllocations)) this.paymentErrors.amount = 'جمع تخصیص با مبلغ برابر نیست'
      return Object.keys(this.paymentErrors).length === 0
    },
    formValidCheque() {
      if (!this.formValid) return false
      return !!(this.chequeForm.number && (this.chequeForm.issue_date || this.paymentForm.payment_date) && this.chequeForm.due_date)
    }
  },
  mounted() {
    this.initLoad()
  },
  watch: {
    'filters.supplier': {
      immediate: true,
      handler(val){
        if (val && val.id){
          // sync payment form supplier
          this.paymentForm.supplier = val.id
          // refresh reports
          this.refreshAllReports()
        }
      }
    }
  },
  methods: {
    // ---------- Utils ----------
    money(v) {
      try { return new Intl.NumberFormat('fa-IR').format(Number(v || 0)) } catch (e) { return v }
    },
    sumArray(arr) { return (arr || []).reduce((a, b) => a + Number(b || 0), 0) },
    badgeClass(s) {
      return { issued: 'bg-yellow-100 text-yellow-800', cleared: 'bg-emerald-100 text-emerald-700', bounced: 'bg-red-100 text-red-700' }[s] || 'bg-gray-100 text-gray-600'
    },
    chequeStatusFa(s) { return {issued: 'صادره', cleared: 'وصول‌شده', bounced: 'برگشتی'}[s] || s },
    supplierName(id) { const f = this.suppliersCache.find(x => x.id === id) || this.suppliers.find(x=>x.id===id); return f ? f.name : id },

    warn(msg){ if (this.warnNotify) this.warnNotify(msg); else alert(msg) },
    success(msg){ if (this.successNotify) this.successNotify(msg); else alert(msg) },

    // Build querystring without empty values
    qs(params){
      const u = new URLSearchParams()
      Object.entries(params || {}).forEach(([k,v])=>{
        if (v === undefined || v === null) return
        const s = String(v).trim()
        if (!s) return
        u.append(k, s)
      })
      return u.toString()
    },

    // CSV export
    exportCSV(name, rows){
      const arr = Array.isArray(rows) ? rows : []
      if (!arr.length) return this.warn('چیزی برای خروجی نیست')
      const keys = Object.keys(arr[0] || {})
      const csv = [keys.join(',')].concat(
          arr.map(r => keys.map(k => `"${((r[k] ?? '')+'').replace(/"/g,'""')}"`).join(','))
      ).join('\n')
      const blob = new Blob([csv], {type:'text/csv;charset=utf-8;'})
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${name}.csv`; a.click(); URL.revokeObjectURL(a.href)
    },
    exportAllCSV(){
      this.exportCSV('ledger', this.ledger.rows)
      this.exportCSV('balances', this.balances)
      this.exportCSV('payments', this.paymentRegister)
    },

    // Supplier flow (no search)
    fetchSuppliersList(){
      this.request({
        url: this.endpoint('main/supplier/all'),
        method: 'GET',
        success: (res)=>{
          // اگر API صفحه‌بندی ندارد، فرض می‌کنیم آرایهٔ ساده است
          let list = Array.isArray(res) ? res : (res.results || [])
          // محدود به حداکثر 10 تا
          list = list.slice(0, 10)
          this.suppliers = list
          // کش برای نمایش نام‌ها در جداول
          list.forEach(s => { if (!this.suppliersCache.find(x=>x.id===s.id)) this.suppliersCache.push(s) })
        }
      })
    },
    onSupplierPicked(){
      const s = this.suppliers.find(x => String(x.id) === String(this.selectedSupplierId))
      this.filters.supplier = s || null
      if (s) this.paymentForm.supplier = s.id
    },
    clearSupplier(){
      this.selectedSupplierId = ''
      this.filters.supplier = null
      this.paymentForm.supplier = ''
      this.ledger = {rows: [], final_balance: '0.00'}
      this.paymentRegister = []
      this.agingData = {current: '0', d30: '0', d60: '0', 'd90+':'0'}
    },

    // Presets
    resetFilters() {
      // supplier باقی می‌ماند، تاریخ‌ها ریست می‌شوند
      this.filters.date_from = ''
      this.filters.date_to = ''
    },
    applyPreset(p) {
      const r = p.range(); this.filters.date_from = r.from; this.filters.date_to = r.to; if (this.hasSupplier) this.refreshAllReports()
    },

    // Bills search in modal
    openBillPicker() {
      if (!this.hasSupplier) return this.warn('ابتدا تأمین‌کننده را انتخاب کنید')
      this.billPicker.open = true
      this.billPicker.q = ''
      this.billPicker.list = []
    },
    debouncedBillSearch() {
      clearTimeout(this.billTimer);
      this.billTimer = setTimeout(() => this.searchBills(), 300)
    },
    searchBills() {
      const params = new URLSearchParams()
      if (this.billPicker.q) params.append('search', this.billPicker.q)
      if (this.filters.supplier?.id) params.append('supplier', this.filters.supplier.id)
      this.billPicker.loading = true
      this.request({
        url: this.endpoint(`accounting/bills/?${params.toString()}`),
        method: 'GET',
        success: (res) => { this.billPicker.list = res; this.billPicker.loading = false },
        error: () => { this.billPicker.loading = false }
      })
    },
    pickBill(b) {
      this.paymentForm.allocations.push({bill: b.id, amount: Number(b.amount_total) || 0})
      this.billPicker.open = false
    },

    // Init & basics
    initLoad() {
      this.fetchSuppliersList()
      this.fetchCurrencies()
      // گزارش‌ها بعد از انتخاب supplier (watcher انجام می‌دهد)
    },
    fetchCurrencies() {
      this.request({
        url: this.endpoint('accounting/currencies/'),
        method: 'GET',
        success: (res) => { this.currencies = res }
      })
    },

    // Reports
    refreshAllReports() {
      if (!this.hasSupplier) return
      this.fetchLedger()
      this.fetchBalances()
      this.fetchPaymentRegister()
      this.fetchAging()
    },
    fetchLedger() {
      if (!this.hasSupplier) return
      this.loading.ledger = true
      const q = this.qs({
        supplier_id: this.filters.supplier?.id,
        date_from: this.filters.date_from,
        date_to: this.filters.date_to
      })
      this.request({
        url: this.endpoint(`accounting/reports/supplier-ledger/${q ? ('?' + q) : ''}`),
        method: 'GET',
        success: (res) => { this.ledger = res; this.loading.ledger = false },
        error:   ()    => { this.loading.ledger = false }
      })
    },
    fetchBalances() {
      if (!this.hasSupplier) { this.balances = []; return }
      this.loading.balances = true
      const q = this.qs({ supplier_id: this.filters.supplier?.id })
      this.request({
        url: this.endpoint(`accounting/reports/supplier-balance/${q ? ('?' + q) : ''}`),
        method: 'GET',
        success: (res) => {
          // اگر API لیست می‌دهد همان؛ اگر یک آبجکت می‌دهد، به آرایه تبدیل می‌کنیم
          this.balances = Array.isArray(res) ? res : [res]
          this.loading.balances = false
        },
        error:   ()    => { this.loading.balances = false }
      })
    },
    fetchPaymentRegister() {
      if (!this.hasSupplier) return
      this.loading.paymentRegister = true
      const q = this.qs({
        supplier_id: this.filters.supplier?.id,
        method: this.paymentFilters.method,
        date_from: this.filters.date_from,
        date_to: this.filters.date_to
      })
      this.request({
        url: this.endpoint(`accounting/reports/payment-register/${q ? ('?' + q) : ''}`),
        method: 'GET',
        success: (res) => { this.paymentRegister = res; this.loading.paymentRegister = false },
        error:   ()    => { this.loading.paymentRegister = false }
      })
    },
    fetchAging() {
      if (!this.hasSupplier) return
      const today = this.aging.today || new Date().toISOString().slice(0, 10)
      const q = this.qs({ supplier_id: this.filters.supplier?.id, today })
      this.request({
        url: this.endpoint(`accounting/reports/supplier-aging/${q ? ('?' + q) : ''}`),
        method: 'GET',
        success: (res) => { this.agingData = res }
      })
    },

    // Payment form helpers
    resetPaymentForm() {
      this.paymentForm = {supplier: this.filters.supplier?.id || '', currency: '', method: 'cash', payment_date: '', amount: 0, allocations: []}
      this.chequeForm = {number: '', bank_name: '', issue_date: '', due_date: ''}
    },
    addAllocRow() { this.paymentForm.allocations.push({bill: '', amount: 0}) },
    removeAllocRow(i) { this.paymentForm.allocations.splice(i, 1) },

    // Submit cash
    submitCashPayment() {
      if (!this.formValid) return this.warn('لطفاً خطاهای فرم را برطرف کنید')
      this.submitting = true
      const payload = {
        supplier: this.paymentForm.supplier,
        currency: this.paymentForm.currency,
        method: 'cash',
        payment_date: this.paymentForm.payment_date,
        amount: this.paymentForm.amount,
        allocations: this.paymentForm.allocations.map(a => ({bill: a.bill, amount: a.amount}))
      }
      const fd = this.fdFrom(payload)
      this.request({
        url: this.endpoint('accounting/payments/'),
        method: 'POST',
        data: fd,
        headers: {'Content-Type': 'multipart/form-data'},
        success: () => {
          this.success('پرداخت نقدی ثبت شد')
          this.resetPaymentForm()
          this.submitting = false
          this.refreshAllReports()
        },
        error: () => { this.submitting = false }
      })
    },

    // Submit cheque
    submitChequePayment() {
      if (!this.formValidCheque) return this.warn('اطلاعات چک کامل نیست')
      this.submitting = true
      const payload = {
        supplier: this.paymentForm.supplier,
        currency: this.paymentForm.currency,
        method: 'cheque',
        payment_date: this.paymentForm.payment_date,
        amount: this.paymentForm.amount,
        allocations: this.paymentForm.allocations.map(a => ({bill: a.bill, amount: a.amount}))
      }
      const fd = this.fdFrom(payload)
      this.request({
        url: this.endpoint('accounting/payments/'),
        method: 'POST',
        data: fd,
        headers: {'Content-Type': 'multipart/form-data'},
        success: (created) => {
          const issue = {
            number: this.chequeForm.number,
            bank_name: this.chequeForm.bank_name,
            issue_date: this.chequeForm.issue_date || this.paymentForm.payment_date,
            due_date: this.chequeForm.due_date,
            allocations: this.paymentForm.allocations.map(a => ({bill: a.bill, amount: a.amount}))
          }
          const fd2 = this.fdFrom(issue)
          this.request({
            url: this.endpoint(`accounting/payments/${created.id}/issue_cheque/`),
            method: 'POST',
            data: fd2,
            headers: {'Content-Type': 'multipart/form-data'},
            success: () => {
              this.success('چک صادر شد')
              this.resetPaymentForm()
              this.submitting = false
              this.refreshAllReports()
            },
            error: () => { this.submitting = false }
          })
        },
        error: () => { this.submitting = false }
      })
    },

    // Cheque actions
    clearCheque() {
      if (!this.chequeAction.id || !this.chequeAction.date) return this.warn('شناسه/تاریخ را وارد کنید')
      const fd = this.fdFrom({clear_date: this.chequeAction.date})
      this.submitting = true
      this.request({
        url: this.endpoint(`accounting/cheques/${this.chequeAction.id}/clear_/`),
        method: 'POST',
        data: fd,
        headers: {'Content-Type': 'multipart/form-data'},
        success: () => { this.success('چک وصول شد'); this.submitting = false; this.refreshAllReports() },
        error:   ()    => { this.submitting = false }
      })
    },
    bounceCheque() {
      if (!this.chequeAction.id || !this.chequeAction.date) return this.warn('شناسه/تاریخ را وارد کنید')
      const fd = this.fdFrom({bounce_date: this.chequeAction.date})
      this.submitting = true
      this.request({
        url: this.endpoint(`accounting/cheques/${this.chequeAction.id}/bounce/`),
        method: 'POST',
        data: fd,
        headers: {'Content-Type': 'multipart/form-data'},
        success: () => { this.success('چک برگشت خورد'); this.submitting = false; this.refreshAllReports() },
        error:   ()    => { this.submitting = false }
      })
    },

    // Global helpers - FormData
    fdFrom(obj) {
      const fd = new FormData();
      Object.keys(obj || {}).forEach(k => {
        const v = obj[k];
        if (v === undefined || v === null) return;
        if (Array.isArray(v)) {
          v.forEach((row, i) => {
            if (row && typeof row === 'object') {
              Object.keys(row).forEach(key => fd.append(`${k}[${i}][${key}]`, row[key]))
            } else {
              fd.append(`${k}[]`, row)
            }
          })
        } else if (typeof v === 'object') {
          Object.keys(v).forEach(key => fd.append(`${k}[${key}]`, v[key]))
        } else {
          fd.append(k, v)
        }
      });
      return fd
    },
  }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .2s }
.fade-enter, .fade-leave-to { opacity: 0 }

::-webkit-scrollbar { height: 8px; width: 8px }
::-webkit-scrollbar-thumb { background: #c7c7c7; border-radius: 999px }
</style>
